import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { PersonAPI } from '@frontend/api-person';
import { ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { AdaptoActions } from '@frontend/adapto-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  Accordion,
  Avatar,
  Chip,
  Text,
  useModalControl,
  ConfirmationModal,
  NotificationBadge,
  useAlert,
  formatDate,
  Button,
  Truncated,
} from '@frontend/design-system';
import {
  APPOINTMENT_STATUS_MAPPING,
  AppointmentStatusEnum,
} from '../../../../../src/components/schedule-calendar-components/appointment-event-card/types';
import { customAccordionStyles } from '../../../../components/location-accordion-list';
import { ScheduleAlertsProvider } from '../../../../context/ScheduleAlertsContext';
import { useGetAppointmentDetails } from '../../../../hooks/use-get-appointment-details';
import { EmptyAlertsGraphic } from './empty-alerts-graphic';
import { useScheduleActionsContext } from './ScheduleActionsContext';
import { ScheduleAlertActionMenu } from './ScheduleAlertActionMenu';

export const ScheduleAlerts = () => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarActions');

  const { refetchAlertCount } = useScheduleActionsContext();
  const { getScopeName, selectedLocationIds } = useAppScopeStore();
  const [selectedLocationId, setSelectedLocationId] = useState<string>(selectedLocationIds[0]);
  const { getLocationTimezone } = useLocationTimezone();

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const { data: scheduleAlerts, refetch: refetchScheduleAlerts } = ScheduleQueries.useGetAllAlerts(selectedLocationId);

  const { mutateAsync: deleteAlert } = ScheduleQueries.useMutateDeleteAlert();

  const handleClearAlert = (id: string, locationId?: string) => {
    deleteAlert({ alertId: id, locationId: locationId ?? '' })
      .then(() => {
        refetchScheduleAlerts();
        refetchAlertCount();
        alert.success(t('Successfully cleared alert'));
      })
      .catch(() => alert.error(t('Error clearing alert')));
  };

  const handleClearAllAlerts = () => {
    const locationName = getScopeName(selectedLocationId);
    if (!!scheduleAlerts) {
      Promise.all(scheduleAlerts.map((alert) => deleteAlert({ alertId: alert.id, locationId: selectedLocationId })))
        .then(() => {
          refetchScheduleAlerts();
          refetchAlertCount();
          alert.success(t('Successfully cleared all alerts for {{locationName}}', { locationName }));
        })
        .catch((error) => {
          console.error(error);
          alert.error(t('Error clearing all alerts for {{locationName}}', { locationName }));
        });
    }
  };

  const getAccordionHeader = (locationId: string, alertCount: number) => {
    return (
      <>
        {alertCount === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            css={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(5) }}
          >
            <EmptyAlertsGraphic size={350} />
            <Text textAlign='center' color='light' weight='regular'>
              {t('Phew. You can take a breather.')}
            </Text>
            <Text textAlign='center' color='light' weight='regular'>
              {t('You have no Schedule Alerts right now.')}
            </Text>
          </motion.div>
        ) : (
          <div css={{ boxShadow: 'none', borderBottom: `2px solid ${theme.colors.neutral5}` }}>
            <section
              css={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: theme.spacing(1, 1.5, 1, 2),
                backgroundColor: theme.colors.white,
                borderRadius: 0,
              }}
            >
              <Text weight='bold' as={'h3'}>
                {t('{{count}} Alerts', { count: alertCount })}
              </Text>
              <Button
                destructive
                iconName='trash'
                variant='tertiary'
                onClick={() => {
                  setSelectedLocationId(locationId);
                  deleteTriggerProps.onClick();
                }}
              >
                {t('Clear All')}
              </Button>
            </section>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <ScheduleAlertsProvider
        refetchScheduleAlerts={refetchScheduleAlerts}
        clearAlert={handleClearAlert}
        setSelectedLocationId={setSelectedLocationId}
      >
        {selectedLocationIds?.length === 1 ? (
          <Accordion
            variant='location'
            showBoxShadow
            chevronSize={16}
            distance={0}
            css={customAccordionStyles.singleContainer}
          >
            {getAccordionHeader(selectedLocationIds[0], scheduleAlerts?.length ?? 0)}
            <section css={scheduleAlertsStyles}>
              {scheduleAlerts?.map((alert) => {
                return <Alert key={alert.id} alert={alert} locationId={selectedLocationIds[0]} />;
              })}
            </section>
          </Accordion>
        ) : (
          <Accordion
            variant='location'
            size='large'
            showBoxShadow
            chevronSize={16}
            css={{ overflowY: 'auto', height: '100%' }}
          >
            {selectedLocationIds?.map((locationId: string) => (
              <AlertContainerMulti
                key={locationId}
                locationId={locationId}
                locationName={getScopeName(locationId)}
                locationTimezone={getLocationTimezone(locationId)}
                getAccordionHeader={getAccordionHeader}
              />
            ))}
          </Accordion>
        )}
      </ScheduleAlertsProvider>
      <ConfirmationModal
        {...deleteModalProps}
        title={t('Clear All Alerts')}
        destructive
        message={t('Are you sure you want to delete all alerts? This action cannot be undone.')}
        confirmLabel={t('Clear All')}
        cancelLabel={t('Cancel')}
        onConfirm={() => handleClearAllAlerts()}
        onCancel={deleteModalProps.onClose}
      />
    </>
  );
};

interface AlertContainerMultiProps {
  locationId: string;
  locationName: string;
  locationTimezone?: string;
  getAccordionHeader: (locationId: string, alertCount: number) => React.ReactNode;
}

const AlertContainerMulti = ({
  locationId,
  locationName,
  locationTimezone,
  getAccordionHeader,
}: AlertContainerMultiProps) => {
  const { data: scheduleAlerts } = ScheduleQueries.useGetAllAlerts(locationId);
  const count = scheduleAlerts?.length;

  return (
    <Accordion.Item key={locationId} value={locationId} css={{ minHeight: '48px' }}>
      <Accordion.Header css={[customAccordionStyles.header, { height: '48px', paddingRight: 0 }]}>
        <Accordion.Header.Location
          title={locationName ?? 'Default'}
          timezone={locationTimezone}
          css={{ textAlign: 'left' }}
        />
        <div css={{ marginLeft: theme.spacing(1) }}>
          {!!count && <NotificationBadge css={{ backgroundColor: theme.colors.primary50 }}>{count}</NotificationBadge>}
        </div>
      </Accordion.Header>
      <Accordion.Body css={customAccordionStyles.body}>
        {getAccordionHeader(locationId, scheduleAlerts?.length ?? 0)}
        <section css={scheduleAlertsStyles}>
          {scheduleAlerts?.map((alert) => {
            return <Alert key={`${locationId}-${alert.id}`} alert={alert} locationId={locationId} />;
          })}
        </section>
      </Accordion.Body>
    </Accordion.Item>
  );
};

interface AlertProps {
  alert: ScheduleTypes.ScheduleAlertType;
  locationId: string;
}

const Alert = ({ alert, locationId }: AlertProps) => {
  const { t } = useTranslation('scheduleCalendarActions');
  const { formatDateAndTime } = useLocationTimezone(locationId);
  const { closeScheduleActionsModal } = useScheduleActionsContext();
  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const [personImg, setPersonImg] = useState<string>('');

  const getPersonPhoto = async () => {
    try {
      const avatarSrc = await PersonAPI.getPersonImage(alert.patient.person_id || '');
      setPersonImg(avatarSrc);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getPersonPhoto();
  }, [alert?.patient?.person_id]);

  const appointmentStatus = APPOINTMENT_STATUS_MAPPING.get(
    alert?.appointment?.status?.toLowerCase().replace(' ', '') as AppointmentStatusEnum
  );

  const { openModal: handleOpenAppointmentModal } = useGetAppointmentDetails({
    appointmentId: alert.appointment?.id || '',
    locationId: locationId || '',
  });

  const fullName = `${alert?.patient.preferred_name ? alert?.patient.preferred_name : alert?.patient.first_name} ${
    alert?.patient.last_name
  }`;

  return (
    <>
      <Accordion
        variant='blank'
        size='large'
        isInteractive
        css={{ borderBottom: `2px solid ${theme.colors.neutral5}` }}
      >
        <Accordion.Item value={alert.id} css={alertAccordionItem}>
          <Accordion.Header
            css={{
              borderBottom: 'none',
            }}
          >
            <div css={headerContent}>
              <header css={alertRowHeader}>
                <Avatar
                  src={personImg}
                  alt={fullName}
                  name={fullName}
                  onClick={() => {
                    setPersonId(alert.patient.person_id, true, locationId);
                    closeScheduleActionsModal();
                  }}
                />
                <Truncated as={Text} weight='bold' css={{ maxWidth: '30vw' }}>
                  {fullName}
                </Truncated>
                {appointmentStatus?.iconName && <Icon name={appointmentStatus?.iconName} css={{ minWidth: 16 }} />}
              </header>
              <section css={alertRowActions}>
                <AdaptoActions maxActions={0} iconColor='default'>
                  <ScheduleAlertActionMenu
                    alert={alert}
                    closeScheduleActionsModal={closeScheduleActionsModal}
                    locationId={locationId}
                    onViewAppointment={handleOpenAppointmentModal}
                  />
                </AdaptoActions>
              </section>
            </div>
          </Accordion.Header>
          <Accordion.Body css={{ paddingTop: 0 }}>
            <div css={bodyContent}>
              <section css={alertData}>
                <div>
                  <Text weight='light' color='light'>
                    {t('Received')}
                  </Text>
                  <Text>{formatDate(alert.created_at, 'MM/DD/YYYY')}</Text>
                </div>
                <div>
                  <Text weight='light' color='light'>
                    {t('Appointment')}
                  </Text>
                  <Text>{formatDateAndTime(alert.appointment.datetime, 'MM/DD/YYYY')}</Text>
                </div>
                <div>
                  <Text weight='light' color='light'>
                    {t('Status')}
                  </Text>
                  <Chip variant={appointmentStatus?.variant} css={{ minWidth: 'fit-content' }}>
                    {appointmentStatus?.text}
                  </Chip>
                </div>
              </section>
              <section>
                <Text css={alertText}>{alert.text}</Text>
                <Text color='light' weight='medium'>
                  {formatDate(alert.created_at, 'h:mm A')}
                </Text>
              </section>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

const scheduleAlertsStyles = css({
  backgroundColor: theme.colors.white,
  overflow: 'auto',
  height: '100%',
});

const headerContent = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1),
});

const alertRowHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
});

const alertRowActions = css({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-end',
});

const bodyContent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingLeft: theme.spacing(6),
});

const alertText = css({
  backgroundColor: theme.colors.neutral10,
  borderRadius: theme.borderRadius.medium,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
});

const alertData = css({
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-start',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
});

const alertAccordionItem = css({
  boxShadow: 'none',
  borderRadius: 0,
  marginBottom: 0,
});
